import React, { useState, useEffect, useRef } from "react";
import "./TheNexus.scss";

/**
 * THE NEXUS - Your Personal Command Ship
 * Not a dashboard. A living, breathing game world.
 */
const TheNexus: React.FC = () => {
    const [shipPower, setShipPower] = useState(0);
    const [activeSystem, setActiveSystem] = useState<"galaxy" | "missions" | "fleet" | "arsenal">("galaxy");
    const [selectedPlanet, setSelectedPlanet] = useState<string | null>(null);
    const [isBooting, setIsBooting] = useState(true);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const audioContext = useRef<AudioContext | null>(null);

    // Ship startup sequence
    useEffect(() => {
        const bootSequence = async () => {
            // Initialize audio
            audioContext.current = new (window.AudioContext || (window as any).webkitAudioContext)();

            // Power up animation
            for (let i = 0; i <= 100; i += 2) {
                setShipPower(i);
                await new Promise(resolve => setTimeout(resolve, 30));
            }

            setTimeout(() => setIsBooting(false), 1500);
        };

        bootSequence();
    }, []);

    // Starfield background
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        canvas.width = window.innerWidth;
        canvas.height = window.innerHeight;

        const stars: Array<{
            x: number;
            y: number;
            z: number;
            prevX?: number;
            prevY?: number;
        }> = [];

        const centerX = canvas.width / 2;
        const centerY = canvas.height / 2;
        const focalLength = canvas.width;

        // Create starfield
        for (let i = 0; i < 800; i++) {
            stars.push({
                x: Math.random() * canvas.width - centerX,
                y: Math.random() * canvas.height - centerY,
                z: Math.random() * focalLength,
            });
        }

        const animate = () => {
            ctx.fillStyle = "rgba(0, 0, 0, 0.1)";
            ctx.fillRect(0, 0, canvas.width, canvas.height);

            stars.forEach((star) => {
                star.z -= 2;
                if (star.z <= 0) {
                    star.z = focalLength;
                    star.x = Math.random() * canvas.width - centerX;
                    star.y = Math.random() * canvas.height - centerY;
                    star.prevX = undefined;
                    star.prevY = undefined;
                }

                const x = star.x / star.z * focalLength + centerX;
                const y = star.y / star.z * focalLength + centerY;
                const size = (1 - star.z / focalLength) * 2;
                const opacity = 1 - star.z / focalLength;

                if (star.prevX && star.prevY) {
                    ctx.strokeStyle = `rgba(255, 255, 255, ${opacity})`;
                    ctx.lineWidth = size;
                    ctx.beginPath();
                    ctx.moveTo(star.prevX, star.prevY);
                    ctx.lineTo(x, y);
                    ctx.stroke();
                }

                star.prevX = x;
                star.prevY = y;
            });

            requestAnimationFrame(animate);
        };

        animate();
    }, []);

    // Play UI sound
    const playSound = (frequency: number, duration: number) => {
        if (!audioContext.current) return;

        const oscillator = audioContext.current.createOscillator();
        const gainNode = audioContext.current.createGain();

        oscillator.connect(gainNode);
        gainNode.connect(audioContext.current.destination);

        oscillator.frequency.value = frequency;
        gainNode.gain.setValueAtTime(0.1, audioContext.current.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.current.currentTime + duration);

        oscillator.start(audioContext.current.currentTime);
        oscillator.stop(audioContext.current.currentTime + duration);
    };

    const planetarySystems = [
        {
            id: "snapple-prime",
            name: "SNAPPLE PRIME",
            faction: "Snapple",
            color: "#FF6B35",
            x: 30,
            y: 40,
            size: 120,
            status: "ACTIVE",
            players: 12847,
            reward: "Fantastic Four Collection",
            timeLeft: "2D 14H",
            threat: "LOW",
        },
        {
            id: "monster-nexus",
            name: "MONSTER NEXUS",
            faction: "Monster Energy",
            color: "#95D600",
            x: 70,
            y: 30,
            size: 150,
            status: "INVASION",
            players: 45291,
            reward: "$25,000 Tournament",
            timeLeft: "5H 32M",
            threat: "CRITICAL",
        },
        {
            id: "razer-station",
            name: "RAZER STATION",
            faction: "Razer",
            color: "#00FF00",
            x: 50,
            y: 70,
            size: 100,
            status: "FORTIFIED",
            players: 8432,
            reward: "Viper V3 Pro",
            timeLeft: "1D 8H",
            threat: "MEDIUM",
        },
    ];

    if (isBooting) {
        return (
            <div className="nexus-boot-sequence">
                <canvas ref={canvasRef} className="starfield-canvas" />
                <div className="boot-content">
                    <div className="ship-schematic">
                        <svg viewBox="0 0 400 200" className="ship-outline">
                            <path
                                d="M200 20 L350 100 L300 180 L100 180 L50 100 Z"
                                fill="none"
                                stroke="#00D4FF"
                                strokeWidth="2"
                                opacity={shipPower / 100}
                            />
                            <circle cx="200" cy="100" r="30" fill="#00D4FF" opacity={shipPower / 100} />
                        </svg>
                    </div>

                    <div className="boot-status">
                        <h1 className="boot-title">NEXUS COMMAND SHIP</h1>
                        <div className="power-bar">
                            <div className="power-fill" style={{ width: `${shipPower}%` }} />
                            <div className="power-glow" style={{ width: `${shipPower}%` }} />
                        </div>
                        <div className="boot-text">
                            {shipPower < 30 && "INITIALIZING CORE SYSTEMS..."}
                            {shipPower >= 30 && shipPower < 60 && "LOADING PILOT PROFILE..."}
                            {shipPower >= 60 && shipPower < 90 && "ESTABLISHING FACTION LINKS..."}
                            {shipPower >= 90 && "READY FOR DEPLOYMENT"}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="the-nexus">
            <canvas ref={canvasRef} className="starfield-canvas" />

            {/* COMMAND BRIDGE HUD */}
            <div className="bridge-hud">
                {/* TOP STATUS BAR */}
                <div className="status-bar">
                    <div className="pilot-info">
                        <div className="pilot-avatar">
                            <img src="https://via.placeholder.com/60" alt="Pilot" />
                            <div className="rank-badge">42</div>
                        </div>
                        <div className="pilot-data">
                            <div className="pilot-name">CMDR DEATHKILLER99</div>
                            <div className="pilot-status">APEX PREDATOR • ONLINE</div>
                        </div>
                    </div>

                    <div className="ship-status">
                        <div className="status-indicator">
                            <span className="label">SHIELDS</span>
                            <div className="status-bar-mini">
                                <div className="bar-fill shields" style={{ width: "87%" }} />
                            </div>
                        </div>
                        <div className="status-indicator">
                            <span className="label">FUEL</span>
                            <div className="status-bar-mini">
                                <div className="bar-fill fuel" style={{ width: "64%" }} />
                            </div>
                            <span className="value">2,847</span>
                        </div>
                        <div className="status-indicator premium">
                            <span className="label">QUANTUM</span>
                            <span className="value">12 💎</span>
                        </div>
                    </div>

                    <div className="alert-system">
                        <div className="alert-item critical">
                            <span className="alert-icon">⚠️</span>
                            <span className="alert-text">GUILD RAID IN PROGRESS</span>
                        </div>
                    </div>
                </div>

                {/* MAIN VIEWPORT */}
                <div className="main-viewport">
                    {/* SYSTEM SELECTOR */}
                    <div className="system-selector">
                        <button
                            className={`system-btn ${activeSystem === "galaxy" ? "active" : ""}`}
                            onClick={() => {
                                setActiveSystem("galaxy");
                                playSound(440, 0.1);
                            }}
                        >
                            <div className="btn-glow" />
                            <span className="btn-icon">🌌</span>
                            <span className="btn-label">GALAXY MAP</span>
                        </button>
                        <button
                            className={`system-btn ${activeSystem === "missions" ? "active" : ""}`}
                            onClick={() => {
                                setActiveSystem("missions");
                                playSound(523, 0.1);
                            }}
                        >
                            <div className="btn-glow" />
                            <span className="btn-icon">📡</span>
                            <span className="btn-label">MISSIONS</span>
                        </button>
                        <button
                            className={`system-btn ${activeSystem === "fleet" ? "active" : ""}`}
                            onClick={() => {
                                setActiveSystem("fleet");
                                playSound(587, 0.1);
                            }}
                        >
                            <div className="btn-glow" />
                            <span className="btn-icon">🚀</span>
                            <span className="btn-label">FLEET</span>
                        </button>
                        <button
                            className={`system-btn ${activeSystem === "arsenal" ? "active" : ""}`}
                            onClick={() => {
                                setActiveSystem("arsenal");
                                playSound(659, 0.1);
                            }}
                        >
                            <div className="btn-glow" />
                            <span className="btn-icon">⚔️</span>
                            <span className="btn-label">ARSENAL</span>
                        </button>
                    </div>

                    {/* GALAXY MAP VIEW */}
                    {activeSystem === "galaxy" &&
            <div className="galaxy-map">
                <div className="map-container">
                    {/* Grid overlay */}
                    <svg className="grid-overlay" viewBox="0 0 100 100">
                        {[...Array(10)].map((_, i) =>
                            <g key={i}>
                                <line x1={i * 10} y1="0" x2={i * 10} y2="100" stroke="#00D4FF" strokeWidth="0.1" opacity="0.2" />
                                <line x1="0" y1={i * 10} x2="100" y2={i * 10} stroke="#00D4FF" strokeWidth="0.1" opacity="0.2" />
                            </g>)}
                    </svg>

                    {/* Planetary Systems */}
                    {planetarySystems.map((planet) =>
                        <div
                            key={planet.id}
                            className={`planet-system ${planet.status.toLowerCase()} ${selectedPlanet === planet.id ? "selected" : ""}`}
                            style={{
                                left: `${planet.x}%`,
                                top: `${planet.y}%`,
                                "--planet-color": planet.color,
                            } as any}
                            onClick={() => {
                                setSelectedPlanet(planet.id);
                                playSound(880, 0.15);
                            }}
                        >
                            <div className="planet-body" style={{ width: planet.size, height: planet.size }}>
                                <div className="planet-core" />
                                <div className="planet-rings" />
                                <div className="planet-glow" />
                                {planet.status === "INVASION" &&
                        <div className="invasion-effect">
                            <div className="invasion-wave" />
                            <div className="invasion-wave" />
                        </div>
                                }
                            </div>

                            <div className="planet-info">
                                <div className="planet-name">{planet.name}</div>
                                <div className="planet-status">{planet.status}</div>
                                <div className="planet-players">{planet.players.toLocaleString()} PILOTS</div>
                            </div>
                        </div>)}

                    {/* Travel lines between planets */}
                    <svg className="travel-routes" viewBox="0 0 100 100">
                        <line x1="30" y1="40" x2="50" y2="70" stroke="#00D4FF" strokeWidth="0.2" opacity="0.5" strokeDasharray="2,2">
                            <animate attributeName="stroke-dashoffset" from="0" to="4" dur="2s" repeatCount="indefinite" />
                        </line>
                        <line x1="50" y1="70" x2="70" y2="30" stroke="#00D4FF" strokeWidth="0.2" opacity="0.5" strokeDasharray="2,2">
                            <animate attributeName="stroke-dashoffset" from="0" to="4" dur="2s" repeatCount="indefinite" />
                        </line>
                    </svg>

                    {/* Your ship location */}
                    <div className="your-ship" style={{ left: "45%", top: "50%" }}>
                        <div className="ship-icon">🚀</div>
                        <div className="ship-trail" />
                    </div>
                </div>

                {/* Planet Details Panel */}
                {selectedPlanet &&
                <div className="planet-details">
                    {planetarySystems.filter(p => p.id === selectedPlanet).map(planet =>
                        <div key={planet.id} className="details-content">
                            <h2 className="planet-title" style={{ color: planet.color }}>{planet.name}</h2>
                            <div className="threat-level">
                                <span className="threat-label">THREAT LEVEL</span>
                                <span className={`threat-value ${planet.threat.toLowerCase()}`}>{planet.threat}</span>
                            </div>

                            <div className="campaign-info">
                                <div className="info-row">
                                    <span className="info-label">FACTION</span>
                                    <span className="info-value">{planet.faction}</span>
                                </div>
                                <div className="info-row">
                                    <span className="info-label">TIME REMAINING</span>
                                    <span className="info-value" style={{ color: planet.color }}>{planet.timeLeft}</span>
                                </div>
                                <div className="info-row">
                                    <span className="info-label">GRAND PRIZE</span>
                                    <span className="info-value">{planet.reward}</span>
                                </div>
                            </div>

                            <button className="launch-button" style={{ background: planet.color }}>
                                <span className="launch-text">LAUNCH MISSION</span>
                                <div className="launch-effect" />
                            </button>
                        </div>)}
                </div>
                }
            </div>
                    }

                    {/* MISSIONS TERMINAL */}
                    {activeSystem === "missions" &&
            <div className="missions-terminal">
                <div className="terminal-screen">
                    <div className="terminal-header">
                        <span className="terminal-title">INCOMING TRANSMISSIONS</span>
                        <div className="signal-strength">
                            <div className="signal-bar active" />
                            <div className="signal-bar active" />
                            <div className="signal-bar active" />
                            <div className="signal-bar" />
                        </div>
                    </div>

                    <div className="mission-feed">
                        <div className="mission-transmission priority">
                            <div className="transmission-source">
                                <img src="https://via.placeholder.com/40" alt="Faction" />
                                <span className="source-name">MONSTER COMMAND</span>
                            </div>
                            <div className="transmission-content">
                                <h3 className="mission-title">EMERGENCY: Clutch Kings Tournament</h3>
                                <p className="mission-brief">Top teams are forming. Your guild rank qualifies for direct entry. Prize pool increased to $25,000.</p>
                                <div className="mission-meta">
                                    <span className="meta-item">⏱️ 5H 32M</span>
                                    <span className="meta-item">👥 45,291 PILOTS</span>
                                    <span className="meta-item">💰 $25,000</span>
                                </div>
                            </div>
                            <button className="accept-button urgent">
                                <span>ACCEPT</span>
                                <div className="button-pulse" />
                            </button>
                        </div>

                        <div className="mission-transmission">
                            <div className="transmission-source">
                                <img src="https://via.placeholder.com/40" alt="Faction" />
                                <span className="source-name">SNAPPLE FLEET</span>
                            </div>
                            <div className="transmission-content">
                                <h3 className="mission-title">Collection Drive: Fantastic Four</h3>
                                <p className="mission-brief">Collect all four limited edition flavors. Bonus rewards for complete set.</p>
                                <div className="mission-progress">
                                    <div className="collection-items">
                                        <div className="item collected">🟦</div>
                                        <div className="item collected">🟥</div>
                                        <div className="item">⬜</div>
                                        <div className="item">⬜</div>
                                    </div>
                                    <span className="progress-text">2/4 COLLECTED</span>
                                </div>
                            </div>
                            <button className="accept-button">CONTINUE</button>
                        </div>

                        <div className="mission-transmission">
                            <div className="transmission-source">
                                <img src="https://via.placeholder.com/40" alt="Faction" />
                                <span className="source-name">RAZER TECH</span>
                            </div>
                            <div className="transmission-content">
                                <h3 className="mission-title">Precision Protocol</h3>
                                <p className="mission-brief">Achieve 80% headshot accuracy across 10 matches. Viper V3 Pro awaits the worthy.</p>
                                <div className="mission-requirements">
                                    <span className="req-item">🎯 Current: 67%</span>
                                    <span className="req-item">📊 Matches: 4/10</span>
                                </div>
                            </div>
                            <button className="accept-button">TRACK</button>
                        </div>
                    </div>
                </div>
            </div>
                    }

                    {/* FLEET COMMAND */}
                    {activeSystem === "fleet" &&
            <div className="fleet-command">
                <div className="fleet-overview">
                    <h2 className="fleet-name">APEX PREDATORS</h2>
                    <div className="fleet-stats">
                        <div className="stat-item">
                            <span className="stat-label">FLEET POWER</span>
                            <span className="stat-value">127,450</span>
                        </div>
                        <div className="stat-item">
                            <span className="stat-label">GLOBAL RANK</span>
                            <span className="stat-value">#42</span>
                        </div>
                        <div className="stat-item">
                            <span className="stat-label">ACTIVE PILOTS</span>
                            <span className="stat-value">12/15</span>
                        </div>
                    </div>
                </div>

                <div className="fleet-formation">
                    <div className="formation-grid">
                        {[...Array(15)].map((_, i) =>
                            <div key={i} className={`ship-slot ${i < 12 ? "occupied" : "empty"}`}>
                                {i < 12
                                    ? <div className="fleet-ship">
                                        <div className="ship-model">🚀</div>
                                        <div className="pilot-tag">PILOT_{i + 1}</div>
                                        <div className="ship-status online" />
                                    </div>
                                    : <div className="empty-slot">
                                        <span>VACANT</span>
                                    </div>
                                }
                            </div>)}
                    </div>

                    <div className="fleet-operation">
                        <h3 className="operation-title">CURRENT OPERATION</h3>
                        <div className="operation-details">
                            <div className="op-timer">
                                <span className="timer-value">02:34:16</span>
                                <span className="timer-label">REMAINING</span>
                            </div>
                            <div className="op-objective">
                                <span className="obj-label">OBJECTIVE</span>
                                <span className="obj-value">Defend Snapple Prime from Monster Invasion</span>
                            </div>
                            <button className="reinforce-btn">SEND REINFORCEMENTS</button>
                        </div>
                    </div>
                </div>
            </div>
                    }
                </div>

                {/* BOTTOM COMMAND PANEL */}
                <div className="command-panel">
                    <div className="quick-actions">
                        <button className="action-btn primary">
                            <span className="btn-icon">🚀</span>
                            <span className="btn-text">QUICK LAUNCH</span>
                            <div className="btn-energy" />
                        </button>

                        <div className="action-group">
                            <button className="action-btn">
                                <span className="btn-icon">📦</span>
                                <span className="btn-badge">3</span>
                            </button>
                            <button className="action-btn">
                                <span className="btn-icon">🏪</span>
                            </button>
                            <button className="action-btn">
                                <span className="btn-icon">🏆</span>
                            </button>
                        </div>
                    </div>

                    <div className="daily-objectives">
                        <h4 className="obj-title">DAILY OBJECTIVES</h4>
                        <div className="obj-list">
                            <div className="obj-item completed">
                                <span className="obj-check">✓</span>
                                <span className="obj-text">Login to Nexus</span>
                                <span className="obj-reward">+100 XP</span>
                            </div>
                            <div className="obj-item active">
                                <div className="obj-progress" style={{ width: "60%" }} />
                                <span className="obj-text">Complete 3 Missions</span>
                                <span className="obj-status">2/3</span>
                            </div>
                            <div className="obj-item">
                                <span className="obj-check">○</span>
                                <span className="obj-text">Win a Fleet Operation</span>
                                <span className="obj-reward">+500 XP</span>
                            </div>
                        </div>
                    </div>

                    <div className="social-feed">
                        <div className="feed-item">
                            <img src="https://via.placeholder.com/30" alt="Pilot" />
                            <span className="feed-text"><strong>Phoenix</strong> achieved Quantum Tier!</span>
                            <span className="feed-time">2m</span>
                        </div>
                        <div className="feed-item">
                            <img src="https://via.placeholder.com/30" alt="Pilot" />
                            <span className="feed-text"><strong>Ghost</strong> won Monster Tournament</span>
                            <span className="feed-time">15m</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* AI COMPANION */}
            <div className="ai-companion">
                <div className="ai-avatar">
                    <div className="ai-core">
                        <div className="core-ring ring-1" />
                        <div className="core-ring ring-2" />
                        <div className="core-ring ring-3" />
                        <div className="core-center">AI</div>
                    </div>
                </div>
                <div className="ai-message">
                    <p>Commander, Monster Nexus is under heavy assault. Your guild needs you!</p>
                    <button className="ai-action">SHOW ME</button>
                </div>
            </div>
        </div>
    );
};

export default TheNexus;